import React, { useContext, useMemo } from 'react';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Divider,
  Flex,
  Form,
  Input,
  Popconfirm,
  Select,
  Switch,
  Typography,
} from 'antd';

import {
  childLevelLabel,
  countDescendants,
  hasRequirements,
  levelLabel,
  MAX_CATEGORIZATION_LEVEL,
  ROLE_OPTIONS,
} from '@totem/components/ticketing/categorizationManagement/categorizationUtilities';
import TicketCategorizationContext from '@totem/components/ticketing/categorizationManagement/TicketCategorizationContext';
import {
  CategorizationCreateRequirements,
  EditableCategorizationNode,
  OrganizationReference,
} from '@totem/components/ticketing/categorizationManagement/types';

const describeRequirements = (
  requirements: CategorizationCreateRequirements,
  orgNames: Map<string, string>,
): string => {
  const parts: string[] = [];
  if (requirements.internalOnly) {
    parts.push('internal only');
  }
  if (requirements.roles.length > 0) {
    parts.push(
      `roles: ${requirements.roles
        .map(
          (role) =>
            ROLE_OPTIONS.find((opt) => opt.value === role)?.label ?? role,
        )
        .join(', ')}`,
    );
  }
  if (requirements.securityGroups.length > 0) {
    parts.push(`groups: ${requirements.securityGroups.join(', ')}`);
  }
  if (requirements.organizations.length > 0) {
    parts.push(
      `organizations: ${requirements.organizations
        .map((orgId) => orgNames.get(orgId) ?? orgId)
        .join(', ')}`,
    );
  }
  return parts.join('; ');
};

const organizationLabel = (org: OrganizationReference) =>
  org.type === 'DEACTIVATED' ? `${org.name} (Deactivated)` : org.name;

const CategorizationNodeEditor = () => {
  const {
    selected,
    organizations,
    securityGroupOptions,
    validationErrors,
    onAddNode,
    onRenameNode,
    onUpdateRequirements,
    onRemoveNode,
    onMoveNode,
  } = useContext(TicketCategorizationContext);

  const orgNames = useMemo(
    () =>
      new Map<string, string>(
        organizations.map((org) => [org.id, organizationLabel(org)]),
      ),
    [organizations],
  );

  const organizationOptions = useMemo(() => {
    const options = organizations.map((org) => ({
      value: org.id,
      label: organizationLabel(org),
    }));
    // Keep ids that no longer resolve to an organization visible so they can be removed.
    selected?.node.createRequirements.organizations
      .filter((orgId) => !orgNames.has(orgId))
      .forEach((orgId) =>
        options.push({
          value: orgId,
          label: `Unknown organization (${orgId})`,
        }),
      );
    return options;
  }, [organizations, orgNames, selected]);

  if (selected === null) {
    return (
      <Typography.Text type="secondary">
        Select a ticket type, category, or sub-category to edit it.
      </Typography.Text>
    );
  }

  const { node, level, ancestors, siblings, index } = selected;
  const label = levelLabel(level);
  const requirements = node.createRequirements;
  const nameError = validationErrors.find((err) => err.key === node.key);
  const isRenamed =
    node.originalName !== null && node.name.trim() !== node.originalName;
  const restrictedAncestors = ancestors.filter(
    (ancestor: EditableCategorizationNode) =>
      hasRequirements(ancestor.createRequirements),
  );
  const descendantCount = countDescendants(node);
  const path = ancestors.map((ancestor) => ancestor.name).join(' / ');

  return (
    <Form layout="vertical">
      <Typography.Title level={5} style={{ marginTop: 0 }}>
        {label}
        {path !== '' && (
          <Typography.Text type="secondary" style={{ fontWeight: 'normal' }}>
            {' '}
            in {path}
          </Typography.Text>
        )}
      </Typography.Title>

      <Form.Item
        label="Name"
        required
        validateStatus={nameError ? 'error' : undefined}
        help={nameError?.message}
      >
        <Input
          value={node.name}
          maxLength={100}
          onChange={(evt) => onRenameNode(node.key, evt.target.value)}
        />
      </Form.Item>

      {isRenamed && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title={`Renaming "${node.originalName}"`}
          description={
            level === 1
              ? 'Existing tickets, task templates and rules keep the old name, and some ticket type names are referenced in code (e.g. Project, Change Request, Service Request). New tickets will use the new name.'
              : 'Existing tickets, task templates and rules keep the old name. New tickets will use the new name.'
          }
        />
      )}

      <Divider titlePlacement="start" plain>
        Who can create tickets with this {label.toLowerCase()}
      </Divider>

      {restrictedAncestors.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          title="Parent restrictions also apply"
          description={restrictedAncestors.map((ancestor) => (
            <div key={ancestor.key}>
              <b>{ancestor.name}:</b>{' '}
              {describeRequirements(ancestor.createRequirements, orgNames)}
            </div>
          ))}
        />
      )}

      <Form.Item
        label="Internal Only"
        extra="Only Intelligent Buildings users can use this."
      >
        <Switch
          checked={requirements.internalOnly}
          onChange={(checked) =>
            onUpdateRequirements(node.key, { internalOnly: checked })
          }
        />
      </Form.Item>

      <Form.Item label="Roles" extra="Leave empty to allow any role.">
        <Select
          mode="multiple"
          allowClear
          placeholder="Any role"
          options={ROLE_OPTIONS}
          value={requirements.roles}
          onChange={(roles: number[]) =>
            onUpdateRequirements(node.key, { roles })
          }
        />
      </Form.Item>

      <Form.Item
        label="Security Groups"
        extra="User must be in at least one of these groups. Leave empty to allow anyone."
      >
        <Select
          mode="tags"
          allowClear
          placeholder="Any security group"
          tokenSeparators={[',']}
          options={securityGroupOptions.map((grp) => ({
            value: grp,
            label: grp,
          }))}
          value={requirements.securityGroups}
          onChange={(securityGroups: string[]) =>
            onUpdateRequirements(node.key, { securityGroups })
          }
        />
      </Form.Item>

      <Form.Item
        label="Organizations"
        extra="Only these organizations can use this. Leave empty to allow all organizations."
      >
        <Select
          mode="multiple"
          allowClear
          showSearch={{ optionFilterProp: 'label' }}
          placeholder="All organizations"
          options={organizationOptions}
          value={requirements.organizations}
          onChange={(orgIds: string[]) =>
            onUpdateRequirements(node.key, { organizations: orgIds })
          }
        />
      </Form.Item>

      <Divider />

      <Flex wrap gap="small">
        {level < MAX_CATEGORIZATION_LEVEL && (
          <Button icon={<PlusOutlined />} onClick={() => onAddNode(node.key)}>
            Add {childLevelLabel(level)}
          </Button>
        )}
        <Button
          icon={<ArrowUpOutlined />}
          disabled={index === 0}
          onClick={() => onMoveNode(node.key, -1)}
        >
          Move Up
        </Button>
        <Button
          icon={<ArrowDownOutlined />}
          disabled={index === siblings.length - 1}
          onClick={() => onMoveNode(node.key, 1)}
        >
          Move Down
        </Button>
        <Popconfirm
          title={`Delete ${label.toLowerCase()} "${node.name}"?`}
          description={
            <>
              {descendantCount > 0 && (
                <div>
                  This also deletes {descendantCount} item
                  {descendantCount === 1 ? '' : 's'} under it.
                </div>
              )}
              <div>Existing tickets keep their current values.</div>
            </>
          }
          okText="Delete"
          okButtonProps={{ danger: true }}
          onConfirm={() => onRemoveNode(node.key)}
        >
          <Button danger icon={<DeleteOutlined />}>
            Delete
          </Button>
        </Popconfirm>
      </Flex>
    </Form>
  );
};

export default CategorizationNodeEditor;
